<template>
  <header class="relative w-full z-50">
    <!-- Main Header Card -->
    <div :class="headerClasses" class="relative z-50">
      
      <!-- Left: Logo (Dynamic) -->
      <div class="flex-shrink-0">
        <h1 class="text-2xl font-lds-heading font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-lds-primary to-lds-accent">
          {{ branding.name }}
        </h1>
      </div>

      <!-- Center: Spaced Out Content -->
      <div class="hidden md:flex flex-1 justify-center items-center gap-12 opacity-90">
        <span class="text-3xl filter drop-shadow-sm">🐾</span> <!-- Paw Print Logo -->
        <span class="font-lds-body text-lds-text-secondary italic font-medium tracking-wide">
          {{ branding.tagline }}
        </span>
      </div>

      <!-- Right: Actions (Capsule Style) -->
      <div class="flex items-center gap-4">
        
        <!-- User Info Text (Left of buttons) -->
        <div v-if="authStore.currentUser" class="flex flex-col items-end mr-2 md:mr-4">
            <span class="text-sm font-bold text-lds-text-main leading-tight">
                {{ authStore.isGuestUser ? 'Welcome Guest' : 'Hi, ' + (authStore.currentUser.displayName || 'User') }}
            </span>
            <span v-if="!authStore.isGuestUser && authStore.currentUser.billing" class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">
                Credits: {{ authStore.currentUser.billing.credits_remaining }} | Used: {{ authStore.currentUser.billing.usage_lifetime || (authStore.currentUser.billing.credits_total - authStore.currentUser.billing.credits_remaining) }}
            </span>
        </div>

        <!-- Guest Button (Only if truly logged out) -->
        <button 
          v-if="!authStore.currentUser"
          @click="handleGuest"
          class="text-sm font-bold text-lds-primary px-4 py-2 rounded-full bg-lds-primary/5 hover:bg-lds-primary/10 transition-colors"
        >
            Use as Guest
        </button>

        <!-- Sign In Button (Logged out OR Guest) - Always Far Right -->
        <button 
           v-if="!authStore.currentUser || authStore.isGuestUser"
           @click="authStore.login"
           class="text-sm font-bold text-white bg-lds-primary px-6 py-2 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all"
        >
            Sign In
        </button>
      </div>

    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue';
import { useTenantStore } from '../../store/tenantStore';
import { useAuthStore } from '../../store/authStore';

const tenantStore = useTenantStore();
const authStore = useAuthStore();

// Variant resolution moved to compute internally or from store to stay prop-less
const variant = computed(() => {
   // The layout engine (App.vue) might pass something via store or we use a standard default
   return 'standard'; 
});

// Variant class mapping
const headerClasses = computed(() => {
    const base = "bg-lds-bg-card transition-all duration-300 w-full px-8 py-4 flex items-center justify-between";
    
    switch(variant.value) {
        case 'minimal':
            return `${base} border-b border-lds-border-default py-2`;
        case 'promo':
            // Removed scale and fixed margins. Orchestrator handles spacing.
            return `${base} shadow-lds-lg rounded-2xl border-2 border-lds-accent`;
        default: // standard
            // Removed mt-6 and mx-auto for fluidity
            return `${base} shadow-lds-md rounded-full border-t-4 border-lds-primary`;
    }
});

// Dynamic Branding with Fallbacks (Sprint 4.2 - Isolation Compliant)
const branding = computed(() => {
    const tenant = tenantStore.currentTenant || {};
    const features = tenant.features || {};
    const header = tenant.header || {};
    
    return {
        name: header.brandName || features.name || 'AnimalMatch',
        tagline: header.tagline || features.tagline || features.description || 'Merging Souls with the Spirit of Nature'
    };
});

const handleGuest = () => {
  authStore.loginAsGuest().then(() => {
    setTimeout(() => {
      const uploadIsland = document.querySelector('[data-island-id="UploadIsland"]');
      if (uploadIsland) uploadIsland.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  });
};
</script>

<style scoped>
/* Ensure gradient text works in more browsers if native Tailwind utility falters */
.bg-clip-text {
  -webkit-background-clip: text;
}
</style>
